/**
 * Estado global de la app y persistencia de borradores. El borrador se guarda
 * tras cada pantalla (H2 de la ronda): un cierre a mitad de captura se retoma
 * al reabrir; los ids (registro, mutación) nacen con el borrador y no cambian.
 * v3: la estación verde tiene dos registros —el viaje y el descarte de
 * recepción— y los dos viven en el mismo store de borradores, tipados.
 */

import { generarUlid } from '../../src/ulid';
import { alCompletar, promesa, type CatalogosLocales, type Entidad, type Preferencias, type Sesion } from '../datos/bd';
import { rotulosEnBandeja } from '../datos/cola';
import {
	anteriorDe,
	desdeDe,
	proyectarSilosAbiertos,
	silosCerradosLocales,
	type BorradorAbrir,
	type BorradorAviso,
	type BorradorCarro,
	type BorradorCerrar,
	type BorradorDescartar,
	type BorradorNota,
	type BorradorParada,
	type BorradorProduccion,
	type BorradorTurno,
	type IntervaloDeSilo,
	type MutacionPendiente,
	type RegistroDeProduccion,
	type PesadaDeSilo,
	type SiloAbierto,
} from '../nucleo/produccion';
import type { LetraCalidad } from '../nucleo/calidad';
import {
	cambiosDeLetra,
	proyeccionDeEmbolsado,
	saldosNoReflejados,
	type BorradorAnularCarga,
	type BorradorAnularPallet,
	type BorradorCorregirCarga,
	type CargaVista,
	type PalletCerradoProyectado,
	type BorradorCambiarLetra,
	type BorradorCerrarPallet,
	type BorradorEmbolsado,
	type BorradorEmbolsar,
	type BorradorNotaPrecinto,
	type BorradorRehacerPallet,
	type MutacionCaida,
	type PalletConocido,
	type PalletProyectado,
	type PrecintoVisto,
	type RotuloElegido,
	type SaldoNoReflejado,
} from '../nucleo/embolsado';
import { avisosVisibles, type AvisoVisible } from '../nucleo/avisos';
import {
	hojaParaCorregir,
	hojasParaCorregir,
	humedadesParaCorregir,
	muestrasSinHumedad,
	silosParaMuestra,
	type BorradorMuestra,
	type HojaParaCorregir,
	type HumedadParaCorregir,
	type MuestraSinHumedad,
	type SiloParaMuestra,
} from '../nucleo/muestra';
import { inicioDelTurno, turnoDe } from '../nucleo/tiempo';
import type { Borrador, BorradorCualquiera, BorradorDescarte } from '../nucleo/viaje';

export type Vista =
	| { tipo: 'cargando' }
	| { tipo: 'enrolar'; error?: string; ocupado?: boolean }
	| { tipo: 'retomar'; borradores: BorradorCualquiera[]; indice?: number }
	/** El reloj del teléfono está corrido: `luego` es la vista a la que se iba (el inicio de la estación, o retomar). */
	| { tipo: 'advertencia-reloj'; desvioMinutos: number; luego: Vista }
	| { tipo: 'captura'; borrador: Borrador }
	| { tipo: 'descarte'; borrador: BorradorDescarte }
	| { tipo: 'guardado'; entidad: Entidad; registroId: string; netoG: number; nroViaje: number | null; enviado: boolean }
	| { tipo: 'pendientes' }
	| { tipo: 'config' }
	| { tipo: 'desenrolado' }
	/** El dispositivo no tiene estación, o tiene una cuyo flujo todavía no existe. */
	| { tipo: 'sin-estacion'; estacion: string | null }
	// v4 · producción: el inicio con los silos abiertos, un flujo en curso, y el acuse.
	| { tipo: 'produccion-inicio'; aviso?: string | null }
	| { tipo: 'produccion'; borrador: BorradorProduccion }
	/** Las filas son las de la revisión al confirmar: recalculadas después de encolar, la bajada recién hecha decía «ya bajó». */
	| { tipo: 'produccion-listo'; borrador: BorradorProduccion; filas: Array<[string, string]> }
	// v4 · calidad: la lista de silos que esperan muestra (con las hojas a medias), la hoja de uno, y el acuse.
	| { tipo: 'calidad-lista'; hojas: BorradorMuestra[]; aviso?: string | null }
	| { tipo: 'calidad-hoja'; borrador: BorradorMuestra }
	/** v6 (§12.9): completar la humedad de una muestra que se guardó sin ella. */
	| { tipo: 'calidad-humedad'; muestra: MuestraSinHumedad; humedad: string; personas: string[]; guardada?: boolean }
	/** §12.17: corregir una humedad ya cargada; `guardada` lleva lo que quedó, para el comprobante. */
	| { tipo: 'calidad-corregir-humedad'; muestra: HumedadParaCorregir; humedad: string; personas: string[]; guardada?: { humedad: number; letra: LetraCalidad; mutacion_id: string } }
	/** §12.18: una hoja de «Cargadas · últimas 48 h» con sus valores; se pinta con la de la lista de ahora, si sigue. */
	| { tipo: 'calidad-ver-hoja'; hoja: HojaParaCorregir }
	/** §12.18: el comprobante de la hoja corregida, con lo que cambió; `mutacion_id` dice si la oficina la rechazó con la pantalla a la vista. */
	| { tipo: 'calidad-hoja-corregida'; borrador: BorradorMuestra; letra: LetraCalidad; cambios: string[]; mutacion_id: string }
	/** La letra es la del payload que se encoló: no se vuelve a proponer con un catálogo que pudo cambiar. */
	| { tipo: 'calidad-listo'; borrador: BorradorMuestra; letra: LetraCalidad }
	// v5 · envasado: el inicio con los pallets en armado, un flujo en curso, y el acuse.
	| { tipo: 'envasado-inicio'; aviso?: string | null }
	| { tipo: 'envasado'; borrador: BorradorEmbolsado }
	| { tipo: 'envasado-listo'; borrador: BorradorEmbolsado }
	/** §12.18: los pallets cerrados de las últimas 48 horas, con lo que puso cada precinto. */
	| { tipo: 'envasado-cerrados' };

/** Acciones inyectadas por main (evita ciclos de import entre pantallas). */
export const acciones = {
	nuevaCaptura: (): void => {
		// main la reemplaza en el arranque
	},
	nuevoDescarte: (): void => {
		// main la reemplaza en el arranque
	},
	/**
	 * La única puerta a «Lo cargado» (el chip de la cola y los botones «Ver lo cargado»): en verde
	 * fuerza un refresco de catálogos antes de pintar «Verde, últimas 24 horas» (§5.5); el pintado
	 * no dispara nada, así que el repintado que trae la respuesta no vuelve a pedir.
	 */
	verLoCargado: (): void => {
		// main la reemplaza en el arranque
	},
	// v4 · producción: main las reemplaza en el arranque.
	inicioProduccion: (): void => {},
	abrirSilo: (): void => {},
	/** Pesa los silos abiertos y los deja abiertos para quien viene (10/9). */
	cambioDeTurno: (): void => {},
	/** Con silo, se entró tocando su tarjeta: ya está elegido. */
	cerrarSilo: (_silo?: SiloAbierto): void => {},
	cambioDeCarro: (): void => {},
	parada: (): void => {},
	/** v6 (§12.2, §12.4): un aviso para las demás encargadas y una nota a un silo abierto. */
	dejarAviso: (): void => {},
	notaASilo: (_silo?: SiloAbierto): void => {},
	// v4 · calidad: main las reemplaza en el arranque.
	inicioCalidad: (): void => {},
	abrirHoja: (_silo: SiloParaMuestra): void => {},
	/** §12.18: ver una hoja de «Cargadas · últimas 48 h», y corregirla (retoma la corrección a medias, si la hay). */
	verHoja: (_hoja: HojaParaCorregir): void => {},
	corregirHoja: (_hoja: HojaParaCorregir): void => {},
	// v5 · envasado: main las reemplaza en el arranque.
	inicioEnvasado: (): void => {},
	/** Con precinto, se entró tocando su tarjeta en el inicio: ya está elegido. */
	embolsar: (_precinto?: PrecintoVisto): void => {},
	/** Con pallet, se entró tocando su tarjeta: ya está elegido. */
	cerrarPallet: (_pallet?: PalletProyectado): void => {},
	/** v5, segunda tanda: la tarima de espera (un precinto sin letra) y «se tira un pallet»; pasarla a un pallet es embolsar el precinto (§12.21). */
	tarimaDeEspera: (): void => {},
	tirarPallet: (_pallet?: PalletProyectado): void => {},
	/** §12.18: los cerrados de las últimas 48 horas, y corregir o anular una carga (del pallet en armado o de uno cerrado). */
	verCerrados: (): void => {},
	corregirCarga: (_carga: CargaVista): void => {},
	anularCarga: (_carga: CargaVista): void => {},
	/** v6 (§12.5, §12.4): desde el precinto elegido. */
	cambiarLetra: (_precinto: PrecintoVisto): void => {},
	notaPrecinto: (_precinto: PrecintoVisto): void => {},
};

export interface App {
	bd: IDBDatabase;
	sesion: Sesion | null;
	catalogos: CatalogosLocales | null;
	preferencias: Preferencias;
	vista: Vista;
	enLinea: boolean;
	contadores: {
		pendientes: number;
		incidentes: number;
		masViejaMs: number | null;
	};
	render: () => void;
	dispararSync: () => void;
	/**
	 * Los silos abiertos que la operaria tiene delante: el catálogo del servidor con
	 * la cola local aplicada encima. Se recalcula tras cada sync y cada guardado, no
	 * en cada render: leer IndexedDB es asincrónico y pintar no lo es.
	 */
	silos: SiloAbierto[];
	/** Los silos que este teléfono abrió y cerró (recibos y cola): «abrir» avisa si la hora choca con uno (2/10). */
	silosCerrados: IntervaloDeSilo[];
	/** Lo que el servidor rechazó y sigue en la bandeja: el motivo, por id de mutación. La pantalla de «listo» lo dice. */
	rechazos: Map<string, string>;
	/** Calidad: los silos que esperan muestra, ya proyectados (catálogo menos lo cargado acá). */
	paraMuestra: SiloParaMuestra[];
	/** Calidad (v6, §12.9): las muestras guardadas sin humedad, menos las que este teléfono ya completó. */
	faltaHumedad: MuestraSinHumedad[];
	/** Calidad (§12.17): la humedad cargada en las últimas 24 h, con lo de este teléfono sin reflejar encima. */
	humedadesParaCorregir: HumedadParaCorregir[];
	/** §12.18: las hojas de las últimas 48 horas, de toda la estación, con lo de este teléfono encima. */
	hojasParaCorregir: HojaParaCorregir[];
	/** Envasado: los pallets en armado y las tarimas en espera, con la cola encima (§5.7 r. 21). */
	pallets: PalletProyectado[];
	/** Envasado (§12.18): los cerrados de las últimas 48 horas, con la cola encima. */
	palletsCerrados: PalletCerradoProyectado[];
	/** Envasado: por precinto, lo cargado que el catálogo no refleja (gramos y formato), aunque el pallet ya se haya cerrado acá. */
	saldosDeEmbolsado: Map<string, SaldoNoReflejado>;
	/** Envasado: letra, formato y número de cada pallet que este teléfono conoce, por `pallet_id`, cerrado o no. */
	palletsConocidos: Map<string, PalletConocido>;
	/**
	 * Envasado: el rótulo elegido por cada borrador a medias, por `capture_id` (§5.7 r. 22): «¿qué
	 * rótulo?» descuenta lo que otro borrador de este teléfono ya eligió. Lo mantienen guardar,
	 * descartar y listar los borradores.
	 */
	rotulosEnBorradores: Map<string, RotuloElegido>;
	/**
	 * Envasado: los rótulos de las aperturas que están en la bandeja, por mutación (§5.7 r. 22): siguen
	 * tomados hasta que el árbol se rehaga o se quite. Lo recalcula `recalcularRotulosEnBandeja` con la
	 * proyección.
	 */
	rotulosEnBandeja: Array<{ mutacion_id: string; campania_id: string; numero: number }>;
	/**
	 * El desvío del reloj con el que se tocó «seguir igual» en esta sesión de la app, o null: el
	 * aviso de reloj corrido (`ui/aviso-reloj.ts`) sale una vez por sesión y vuelve si el desvío
	 * cambia más que el umbral. Vive en memoria a propósito: «Ya la arreglé» recarga y vuelve a mirar.
	 */
	relojAceptadoMs: number | null;
	/** v6 (§12.2): los avisos que se muestran arriba del inicio de producción, ya proyectados. */
	avisos: AvisoVisible[];
	/** v6 (§12.2, §12.4): los avisos y las notas de la cola y los recibos sin reflejar (las notas se proyectan por silo al pintar). */
	mutacionesDeAvisos: MutacionPendiente[];
	/** v6 (§12.2): los avisos ajenos marcados «Entendido» en este teléfono (`config.avisos_entendidos`). */
	avisosEntendidos: Set<string>;
	/** v6 (§12.5): las versiones de muestra de este teléfono que el catálogo no refleja (cambios de letra y humedades ya aplicadas), por la que reemplazan. */
	cambiosDeLetra: Map<string, { muestra_id: string; letra: string | null }>;
	/** v6 (§12.3): los viajes de este teléfono que el catálogo todavía no refleja (para «no figura bajado»). */
	viajesLocales: MutacionPendiente[];
	/** v6 (§12.1, 6/10): las bajadas de carro de este teléfono que el catálogo todavía no refleja (para «ya bajó»). */
	bajadasLocales: MutacionPendiente[];
}

export let app: App;

export function iniciarApp(
	base: Omit<
		App,
		'vista' | 'silos' | 'silosCerrados' | 'rechazos' | 'paraMuestra' | 'pallets' | 'palletsCerrados' | 'saldosDeEmbolsado' | 'palletsConocidos' | 'rotulosEnBorradores' | 'rotulosEnBandeja' | 'relojAceptadoMs' | 'avisos' | 'mutacionesDeAvisos' | 'avisosEntendidos' | 'cambiosDeLetra' | 'viajesLocales' | 'bajadasLocales' | 'faltaHumedad' | 'humedadesParaCorregir' | 'hojasParaCorregir'
	> & {
		silos?: SiloAbierto[];
		paraMuestra?: SiloParaMuestra[];
		pallets?: PalletProyectado[];
	},
): App {
	app = {
		...base,
		silos: base.silos ?? [],
		silosCerrados: [],
		rechazos: new Map(),
		paraMuestra: base.paraMuestra ?? [],
		faltaHumedad: [],
		humedadesParaCorregir: [],
		hojasParaCorregir: [],
		pallets: base.pallets ?? [],
		palletsCerrados: [],
		saldosDeEmbolsado: new Map(),
		palletsConocidos: new Map(),
		rotulosEnBorradores: new Map(),
		rotulosEnBandeja: [],
		relojAceptadoMs: null,
		avisos: [],
		mutacionesDeAvisos: [],
		avisosEntendidos: new Set(),
		cambiosDeLetra: new Map(),
		viajesLocales: [],
		bajadasLocales: [],
		vista: { tipo: 'cargando' },
	};
	return app;
}

/** Los silos abiertos ya proyectados. La pantalla los lee de acá, sin tocar la base. */
export function silosAbiertos(): SiloAbierto[] {
	return app.silos;
}

/** El número pintado en el silo físico, o «—» si el catálogo no lo tiene. */
export function numeroDeSilo(silo: Pick<SiloAbierto, 'silo_fisico_id'>): string {
	const fila = (app.catalogos?.silos ?? []).find((s) => s.id === silo.silo_fisico_id);
	return fila ? String(fila.numero) : '—';
}

/**
 * Rehace la proyección con lo que haya en la cola y en los recibos. `historia` es todo lo de
 * producción del teléfono, sin recortar por el catálogo: de ahí salen los silos ya cerrados.
 */
export function recalcularSilos(mutaciones: MutacionPendiente[], historia: RegistroDeProduccion[]): void {
	app.silos = proyectarSilosAbiertos(app.catalogos?.llenados_abiertos ?? [], mutaciones);
	app.silosCerrados = silosCerradosLocales(historia, app.silos);
	// v6 (§12.1, 6/10): la bajada recién encolada ya cuenta para «ya bajó» debajo del carro.
	app.bajadasLocales = mutaciones.filter((m) => m.tipo === 'cambio_carro.crear');
}

/** v6 (§12.2, §12.4): rehace los avisos visibles con la cola, los recibos y los «Entendido». */
export function recalcularAvisos(mutaciones: MutacionPendiente[]): void {
	app.mutacionesDeAvisos = mutaciones;
	app.avisos = avisosVisibles(app.catalogos?.avisos_turno ?? [], mutaciones, app.avisosEntendidos, ahoraMs(), app.sesion?.dispositivo_id ?? null);
}

/** Lo mismo para calidad: el catálogo de «sin muestra» menos las muestras que este teléfono ya cargó. */
export function recalcularParaMuestra(mutaciones: MutacionPendiente[]): void {
	// v6 (§12.5): las mismas mutaciones de muestra traen los cambios de letra de envasado.
	app.cambiosDeLetra = cambiosDeLetra(mutaciones);
	app.paraMuestra = silosParaMuestra(app.catalogos?.llenados_para_muestra ?? [], mutaciones);
	app.faltaHumedad = muestrasSinHumedad(app.catalogos?.muestras_sin_humedad ?? [], mutaciones);
	app.humedadesParaCorregir = humedadesParaCorregir(app.catalogos?.humedades_recientes, mutaciones);
	app.hojasParaCorregir = hojasParaCorregir(app.catalogos?.hojas_recientes, mutaciones);
}

/** Envasado: los pallets abiertos ya proyectados. */
export function palletsAbiertos(): PalletProyectado[] {
	return app.pallets;
}

/**
 * Lo mismo para envasado: el catálogo de pallets abiertos con la cola y los recibos encima. Una
 * carga local toma la letra de la muestra y el precinto de `llenados_para_envasar` (el catálogo no
 * la trae): con la letra tal como la dice la muestra —`descarte` incluido—, que es lo que el
 * servidor manda en las cargas que sí trae, para que «calidad cambió» compare lo mismo.
 */
export function recalcularPallets(mutaciones: MutacionPendiente[]): void {
	const paraEnvasar = app.catalogos?.llenados_para_envasar ?? [];
	const paraTarima = app.catalogos?.llenados_para_tarima ?? [];
	const proyeccion = proyeccionDeEmbolsado(
		app.catalogos?.pallets_abiertos ?? [],
		mutaciones,
		{
			letraDe: (id) => paraEnvasar.find((l) => l.llenado_id === id)?.letra ?? null,
			precintoDe: (id) => paraEnvasar.find((l) => l.llenado_id === id)?.precinto ?? paraTarima.find((l) => l.llenado_id === id)?.precinto ?? null,
		},
		app.catalogos?.pallets_cerrados_recientes?.pallets ?? [],
	);
	app.pallets = proyeccion.pallets;
	app.palletsCerrados = proyeccion.cerrados;
	app.palletsConocidos = proyeccion.palletsConocidos;
	app.saldosDeEmbolsado = saldosNoReflejados(proyeccion.cargas);
}

/** Los rótulos de las aperturas caídas, leídos de la bandeja: va con cada recálculo de la proyección. */
export async function recalcularRotulosEnBandeja(): Promise<void> {
	app.rotulosEnBandeja = await rotulosEnBandeja(app.bd);
}

export function irA(vista: Vista): void {
	app.vista = vista;
	app.render();
}

/** «Ahora» calibrado: reloj local + desvío medido contra hora_servidor (H6). */
export function ahoraMs(): number {
	return Date.now() + (app.preferencias.desvio_ms ?? 0);
}

/** La campaña abierta según los catálogos locales, o null si no hay ninguna. */
export function campaniaAbiertaId(): string | null {
	const abiertas = (app.catalogos?.campanias ?? []).filter((c) => c.fecha_fin === null);
	return abiertas.sort((a, b) => b.anio - a.anio)[0]?.id ?? null;
}

/**
 * El «¿quién?» de verde viene contestado con el dueño del teléfono (8/9, reemplaza la
 * decisión del 7/9 de que verde siguiera preguntando): el teléfono es personal y no se
 * presta, así que si está cargando es porque esa persona está descargando. Lo que puede
 * faltar es el compañero, y a eso apunta el copy de la pantalla.
 *
 * No se premarca nada si el teléfono es compartido (el muleto se enrola sin persona) ni si
 * la persona quedó dada de baja: en los dos casos la pantalla pregunta como antes. La
 * identidad sigue siendo POR REGISTRO: lo que se toca es quién MÁS descargó. Al
 * dueño no se lo saca (`alternarPersona`), porque el teléfono es suyo y si está
 * cargando, está trabajando.
 */
export function personaPremarcada(): string[] {
	const id = app.sesion?.persona_habitual_id ?? null;
	if (id === null) return [];
	return (app.catalogos?.personas ?? []).some((p) => p.id === id && p.activo === 1) ? [id] : [];
}

/**
 * Quiénes llevan el descarte al monte según gestión (v4.2, 13/9: una sola; 0004 del 6/10, §12.13:
 * una o dos —Nicolás: «una o más»; hoy Darío y Carlos—), las que siguen activas, en el orden del
 * catálogo (por nombre) y cortadas a dos: los dos lugares del descarte. Gestión no deja marcar a
 * una tercera, pero el corte es el cinturón.
 */
export function quienesLlevanElDescarte(): string[] {
	return (app.catalogos?.personas ?? [])
		.filter((p) => p.activo === 1 && p.lleva_descarte === 1)
		.map((p) => p.id)
		.slice(0, 2);
}

/** ¿El dueño del teléfono sigue entre los elegidos? Decide el copy del «¿quién?». */
export function conPersonaHabitual(elegidas: string[]): boolean {
	const id = app.sesion?.persona_habitual_id ?? null;
	return id !== null && elegidas.includes(id);
}

export function nuevoBorrador(precarga?: Partial<Borrador>): Borrador {
	return {
		tipo: 'viaje',
		capture_id: generarUlid(),
		viaje_id: generarUlid(),
		mutacion_id: generarUlid(),
		campania_id: campaniaAbiertaId(),
		paso: 0,
		personas: personaPremarcada(),
		lote_id: null,
		carros: [],
		corte_ms: null,
		entrada_ms: null,
		bruto_kg: null,
		nota: null,
		actualizado_en: Date.now(),
		...precarga,
	};
}

export function nuevoBorradorDescarte(precarga?: Partial<BorradorDescarte>): BorradorDescarte {
	// Gestión marca quiénes llevan el descarte al monte (una o dos, 6/10, §12.13; antes una sola,
	// 13/9): el borrador nace con ellas y directo en los carros; «cambiar» en la revisión es la
	// excepción. Sin marca, como el viaje: la dueña del teléfono premarcada y la pregunta.
	const llevan = quienesLlevanElDescarte();
	return {
		tipo: 'descarte',
		capture_id: generarUlid(),
		descarte_id: generarUlid(),
		mutacion_id: generarUlid(),
		campania_id: campaniaAbiertaId(),
		paso: llevan.length === 0 ? 0 : 1,
		personas: llevan.length === 0 ? personaPremarcada() : llevan,
		bruto_kg: null,
		carros: [],
		registrado_ms: null,
		nota: null,
		actualizado_en: Date.now(),
		...precarga,
	};
}

/** El horno se elige solo cuando hay uno activo; con dos, la pantalla lo pregunta. */
function hornoUnico(): string | null {
	const activos = (app.catalogos?.hornos ?? []).filter((h) => h.activo === 1);
	return activos.length === 1 ? activos[0].id : null;
}

/**
 * Lo común a los seis flujos. Nacen en el paso 0: qué se saltea —el «¿quién?» ya
 * contestado, el silo elegido desde la tarjeta, el horno único— lo decide
 * `arrancarEn` (pantallas-produccion) al arrancar, y es la única fuente de verdad
 * (segunda pasada, P12, 12/9: acá había un `1` a ciegas que competía con ella).
 */
function comunProduccion() {
	const personas = personaPremarcada();
	return { capture_id: generarUlid(), mutacion_id: generarUlid(), personas, paso: 0, actualizado_en: Date.now() };
}

export function nuevoBorradorAbrir(): BorradorAbrir {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'abrir',
		llenado_id: generarUlid(),
		version_id: generarUlid(),
		campania_id: campaniaAbiertaId(),
		silo_fisico_id: null,
		lote_campo_id: null,
		trieur_id: null,
		horno_id: hornoUnico(),
		precinto: '',
		tara_kg: null,
		abierto_ms: null,
		nota: null,
		foto: null,
	};
}

/**
 * La foto de un silo abierto para pesarlo: lo que la pantalla muestra y lo que el
 * payload necesita, congelados al arrancar. Si otro teléfono lo pesa mientras esta
 * carga está a medias, la revisión sigue mostrando lo que la operaria eligió.
 */
function fotoDePesada(silo: SiloAbierto): PesadaDeSilo {
	return {
		parcial_id: generarUlid(),
		mutacion_id: generarUlid(),
		llenado_id: silo.llenado_id,
		silo_numero: numeroDeSilo(silo),
		precinto: silo.precinto,
		neto_visto_g: silo.ultimo_bruto_g === null ? null : silo.ultimo_bruto_g - silo.tara_base_g,
		// De qué depende: de la última pesada si sigue en la cola, y si no, de la
		// apertura. El `inicio_en` congelado es el fin de esa pesada: si ella se rechaza,
		// esta tiene que caer con ella y no viajar con un arranque que no existe
		// (Codex, 11/9). Esperarla cuesta un sync más; un hueco en la cadena, un dato.
		padre_mutacion_id: silo.ultimo_parcial_mutacion_id ?? silo.abierto_mutacion_id,
		abierto_en: silo.abierto_en,
		inicio_en: desdeDe(silo),
		anterior_g: anteriorDe(silo),
		tara_base_g: silo.tara_base_g,
		bruto_kg: null,
	};
}

/** El cambio de turno pesa TODOS los abiertos de ahora: uno o dos, nunca más. */
export function nuevoBorradorTurno(silos: SiloAbierto[]): BorradorTurno {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'turno',
		silos: silos.map(fotoDePesada),
		// El turno viene propuesto por la hora desde que nace el borrador: fijarlo al
		// pintar la pantalla guardaba y repintaba en medio del pintado (11/9).
		turno: turnoDe(ahoraMs()),
		hasta_ms: null,
		recibe_id: null,
		nota: null,
		aviso_texto: null,
		aviso_id: generarUlid(),
		aviso_mutacion_id: generarUlid(),
	};
}

export function nuevoBorradorCerrar(): BorradorCerrar {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'cerrar',
		desde_inicio: false,
		silo_numero: null,
		precinto: null,
		neto_visto_g: null,
		padre_mutacion_id: null,
		llenado_id: null,
		version_id: generarUlid(),
		version_base_id: null,
		// El peso final es un parcial más, con identidad propia desde el borrador.
		parcial_id: generarUlid(),
		parcial_mutacion_id: generarUlid(),
		abierto_en: null,
		inicio_en: null,
		anterior_g: null,
		tara_base_g: null,
		bruto_kg: null,
		cerrado_ms: null,
		ultimo_parcial_id: null,
		nota_texto: null,
		nota_id: generarUlid(),
		nota_mutacion_id: generarUlid(),
	};
}

/** v6 (§12.2): el aviso nace con su id; la hora es la de la confirmación. */
export function nuevoBorradorAviso(): BorradorAviso {
	return { ...comunProduccion(), tipo: 'aviso', aviso_id: generarUlid(), campania_id: campaniaAbiertaId(), texto: null };
}

/** v6 (§12.4): una nota a un silo abierto; si se entra desde un silo, nace con él. */
export function nuevoBorradorNota(): BorradorNota {
	return { ...comunProduccion(), tipo: 'nota', nota_id: generarUlid(), llenado_id: null, silo: null, precinto_del_silo: null, padre_mutacion_id: null, texto: null };
}

export function nuevoBorradorDescartar(): BorradorDescartar {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'descartar',
		silo_numero: null,
		precinto: null,
		neto_visto_g: null,
		padre_mutacion_id: null,
		llenado_id: null,
		version_id: generarUlid(),
		version_base_id: null,
		// Descartar un silo abierto exige hora de cierre (§5.2.9): se propone «ahora»
		// —con el reloj calibrado, como todo lo demás— y se revisa en la última pantalla,
		// fila «Descartado a las»; el paso de hora existe para corregirla desde ahí y no
		// está en el camino de ida (segunda pasada, P11, 12/9).
		cerrado_ms: ahoraMs(),
		motivo: null,
	};
}

export function nuevoBorradorCarro(): BorradorCarro {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'carro',
		cambio_carro_id: generarUlid(),
		campania_id: campaniaAbiertaId(),
		carro_id: null,
		registrado_ms: null,
		nota: null,
	};
}

export function nuevoBorradorParada(): BorradorParada {
	const comun = comunProduccion();
	return {
		...comun,
		tipo: 'parada',
		parada_id: generarUlid(),
		campania_id: campaniaAbiertaId(),
		motivo_parada_id: null,
		minutos: null,
		// Propuesto por la hora al nacer, y fechado en el arranque de ese turno (una
		// parada anotada a las 06:10 es de la noche, que empezó ayer a las 22:00).
		turno: turnoDe(ahoraMs()),
		registrado_ms: inicioDelTurno(ahoraMs(), turnoDe(ahoraMs())),
		nota: null,
	};
}

/**
 * Los tres borradores de envasado. Como en producción, nacen en el paso 0 y `arrancarEn`
 * (pantallas-envasado) decide qué se saltea. La hora se propone al nacer —con el reloj
 * calibrado— y se revisa en la última pantalla (P11): el paso de hora no está en el camino de
 * ida. Quiénes embolsan (`personas_pallet`) es lo que va al pallet, hasta cuatro; `personas`
 * es quien carga ESTE registro, como en todas las estaciones.
 */
export function nuevoBorradorEmbolsar(): BorradorEmbolsar {
	return {
		...comunProduccion(),
		tipo: 'embolsar',
		desde_inicio: false,
		tarima: false,
		envasado_id: generarUlid(),
		precinto: null,
		formato_bolsa_g: null,
		bolsas: null,
		destino: null,
		nuevo: null,
		personas_pallet: [],
		registrado_ms: ahoraMs(),
		nota: null,
	};
}

/**
 * Rehacer con otro rótulo una apertura que el servidor rechazó por el rótulo (§5.7 r. 22): nace
 * desde la bandeja con la apertura caída y lo que colgaba de ella, y solo pregunta el rótulo.
 * `capture_id` es el id de la mutación caída: un borrador por incidente.
 */
export function nuevoBorradorRehacerPallet(apertura: MutacionCaida, hijos: MutacionCaida[], motivo: string): BorradorRehacerPallet {
	return {
		...comunProduccion(),
		capture_id: apertura.id,
		tipo: 'rehacer_pallet',
		apertura,
		hijos,
		motivo,
		rotulo: null,
	};
}

export function nuevoBorradorCerrarPallet(): BorradorCerrarPallet {
	return {
		...comunProduccion(),
		tipo: 'cerrar_pallet',
		desde_inicio: false,
		pallet: null,
		version_id: generarUlid(),
		cerrado_ms: ahoraMs(),
	};
}

/** «Se tira un pallet» (§5.7 r. 17c): la hora se propone ahora; el motivo se pregunta. */
/** v6 (§12.5): «Cambiar la calidad» de un precinto, desde el precinto elegido. */
export function nuevoBorradorCambiarLetra(p: PrecintoVisto): BorradorCambiarLetra {
	return {
		...comunProduccion(),
		tipo: 'cambiar_letra',
		muestra_id: generarUlid(),
		muestra_base_id: p.muestra_id ?? null,
		llenado_id: p.llenado_id,
		precinto: p.precinto,
		silo_numero: p.silo_numero,
		letra_actual: p.letra === 'D' ? 'descarte' : ((p.letra as BorradorCambiarLetra['letra_actual']) ?? null),
		letra: null,
		motivo: null,
	};
}

/** v6 (§12.4): una nota al precinto desde envasado. */
export function nuevoBorradorNotaPrecinto(p: PrecintoVisto): BorradorNotaPrecinto {
	return { ...comunProduccion(), tipo: 'nota_precinto', nota_id: generarUlid(), llenado_id: p.llenado_id, precinto: p.precinto, texto: null };
}

/** §12.18: corregir las bolsas de una carga, con la carga ya elegida (se entra tocándola). */
export function nuevoBorradorCorregirCarga(carga: CargaVista): BorradorCorregirCarga {
	return { ...comunProduccion(), tipo: 'corregir_carga', carga, envasado_id: generarUlid(), pallet_version_id: generarUlid(), bolsas: null };
}

/** §12.18: anular una carga, con la carga ya elegida. */
export function nuevoBorradorAnularCarga(carga: CargaVista): BorradorAnularCarga {
	return { ...comunProduccion(), tipo: 'anular_carga', carga, pallet_version_id: generarUlid(), motivo: null, motivo_otro: null };
}

export function nuevoBorradorAnularPallet(): BorradorAnularPallet {
	return {
		...comunProduccion(),
		tipo: 'anular_pallet',
		desde_inicio: false,
		pallet: null,
		version_id: generarUlid(),
		motivo: null,
		cerrado_ms: ahoraMs(),
	};
}

/**
 * La hoja de un silo, nueva. `capture_id` es el `llenado_id`: un borrador por silo
 * (`obtenerHoja` busca por esa clave). La analista viene premarcada como en el resto
 * de las estaciones —la dueña del teléfono— y, en el muleto, la última que cargó
 * (boceto 7/9: «viene marcada Silvina, que casi nunca falta»).
 */
export function nuevaHoja(silo: SiloParaMuestra): BorradorMuestra {
	return {
		tipo: 'muestra',
		capture_id: silo.llenado_id,
		mutacion_id: generarUlid(),
		muestra_id: generarUlid(),
		llenado_id: silo.llenado_id,
		campania_id: silo.campania_id,
		silo_numero: numeroDeSilo(silo),
		precinto: silo.precinto,
		lote: (app.catalogos?.lotes_campo ?? []).find((l) => l.id === silo.lote_campo_id)?.nombre ?? '—',
		neto_g: silo.neto_g,
		cerrado_en: silo.cerrado_en,
		personas: analistaPremarcada(),
		densidad: '',
		humedad: '',
		tamiz_sobre_3_5: null,
		tamiz_sobre_6: '',
		tamiz_sobre_40: '',
		tamiz_bajo_40: '',
		palos: null,
		contaminacion_grave: null,
		defectos: {},
		defecto_otro: null,
		letra_manual: null,
		motivo: '',
		nota: null,
		pantalla: 'hoja',
		campo: null,
		// La toma es cuando la hoja se abre: el silo llegó al laboratorio. Congelada acá,
		// sobrevive a retomar y a la jornada que corta a las 06:00.
		tomada_ms: ahoraMs(),
		regla_version: null,
		actualizado_en: Date.now(),
	};
}

/** La analista que viene marcada: la dueña del teléfono o, en el muleto, la última que cargó si sigue activa. */
function analistaPremarcada(): string[] {
	const premarcada = personaPremarcada();
	const ultima = app.preferencias.ultima_analista_id ?? null;
	const ultimaActiva = ultima !== null && (app.catalogos?.personas ?? []).some((p) => p.id === ultima && p.activo === 1);
	return premarcada.length > 0 ? premarcada : ultimaActiva ? [ultima as string] : [];
}

/**
 * §12.18: la corrección de una hoja, nueva: un `capture_id` propio (no choca con el alta del mismo silo) e ids nuevos para
 * la versión y la mutación. Quien corrige viene marcada como en una hoja nueva: su `persona_id` va en la versión nueva.
 */
export function nuevaCorreccion(hoja: HojaParaCorregir): BorradorMuestra {
	return hojaParaCorregir(hoja, { capture_id: generarUlid(), mutacion_id: generarUlid(), muestra_id: generarUlid() }, analistaPremarcada(), ahoraMs());
}

/** La hoja guardada de un silo, si la hay. */
export async function leerHoja(llenadoId: string): Promise<BorradorMuestra | undefined> {
	const fila = (await promesa(app.bd.transaction('borradores').objectStore('borradores').get(llenadoId))) as BorradorCualquiera | undefined;
	return fila?.tipo === 'muestra' ? fila : undefined;
}

/** Todas las hojas a medias, la más vieja primero. */
export async function listarHojas(): Promise<BorradorMuestra[]> {
	return (await listarBorradores()).filter((b): b is BorradorMuestra => b.tipo === 'muestra');
}

/** El rótulo que un borrador de envasado tiene elegido, si lo tiene (§5.7 r. 22). */
export function rotuloDeBorrador(borrador: BorradorCualquiera): RotuloElegido | null {
	if (borrador.tipo === 'rehacer_pallet') return borrador.rotulo;
	if (borrador.tipo === 'embolsar') return borrador.nuevo?.rotulo ?? null;
	return null;
}

function anotarRotulo(borrador: BorradorCualquiera): void {
	const rotulo = rotuloDeBorrador(borrador);
	if (rotulo) app.rotulosEnBorradores.set(borrador.capture_id, rotulo);
	else app.rotulosEnBorradores.delete(borrador.capture_id);
}

export async function guardarBorrador(borrador: BorradorCualquiera): Promise<void> {
	borrador.actualizado_en = Date.now();
	const tx = app.bd.transaction('borradores', 'readwrite');
	tx.objectStore('borradores').put(borrador);
	await alCompletar(tx);
	anotarRotulo(borrador);
}

const TIPOS_DE_BORRADOR = new Set<string>(['viaje', 'descarte', 'abrir', 'turno', 'cerrar', 'descartar', 'carro', 'parada', 'aviso', 'nota', 'cambiar_letra', 'nota_precinto', 'muestra', 'embolsar', 'cerrar_pallet', 'rehacer_pallet', 'anular_pallet', 'corregir_carga', 'anular_carga']);
/**
 * Tipos que un shell anterior guardaba y este ya no tiene: `pallet` es el flujo aparte de abrir un
 * pallet, que se sacó el 2/10 (estaba en producción desde esa tarde), y `unir_tarima` el de pasar una
 * tarima a un pallet, que se fue el 27/10 (§12.21: el precinto se embolsa con sus bolsas en espera). Un
 * borrador así no se puede retomar ni descartar desde la pantalla, y quedaba escondido para siempre en
 * la base (Codex, ronda del diff, 2/10): se purga al listar. Lo demás que este shell no conoce se queda
 * (puede ser de un shell más nuevo en otra pestaña).
 */
const TIPOS_RETIRADOS = new Set<string>(['pallet', 'unir_tarima']);

/** Los borradores de envasado, tipados: lo que la vista `envasado` recibe. */
export function esBorradorDeEnvasado(b: BorradorCualquiera): b is BorradorEmbolsado {
	return estacionDeBorrador(b.tipo) === 'envasado';
}

/** A qué estación pertenece un borrador, por su tipo. */
export function estacionDeBorrador(tipo: BorradorCualquiera['tipo']): string {
	switch (tipo) {
		case 'viaje':
		case 'descarte':
			return 'verde';
		case 'muestra':
			return 'calidad';
		case 'embolsar':
		case 'cerrar_pallet':
		case 'rehacer_pallet':
		case 'anular_pallet':
		case 'cambiar_letra':
		case 'nota_precinto':
		case 'corregir_carga':
		case 'anular_carga':
			return 'envasado';
		default:
			return 'produccion';
	}
}

/**
 * Lo que quedó a medias EN ESTA ESTACIÓN. Un borrador de un tipo que este shell no conoce no se
 * ofrece: pintarlo rompería el arranque. Y uno de otra estación tampoco: un teléfono que gestión
 * pasó de envasado a producción no puede retomar un embolsado y mandarlo a `estacion_no_autorizada`
 * (Codex, ronda del diff del paso 7, 14/9). Los dos quedan en la base, sin molestar: si el
 * teléfono vuelve a su estación, vuelven a ofrecerse.
 */
export async function listarBorradores(): Promise<BorradorCualquiera[]> {
	const todas = (await promesa(app.bd.transaction('borradores').objectStore('borradores').getAll())) as BorradorCualquiera[];
	const retirados = todas.filter((f) => TIPOS_RETIRADOS.has(f.tipo));
	if (retirados.length > 0) {
		const tx = app.bd.transaction('borradores', 'readwrite');
		for (const f of retirados) tx.objectStore('borradores').delete(f.capture_id);
		await alCompletar(tx).catch((error) => console.error('no se pudo purgar un borrador retirado', error));
	}
	const filas = todas.filter((f) => !TIPOS_RETIRADOS.has(f.tipo));
	// Los rótulos elegidos, de todos los borradores guardados: es la lectura que los pone al día.
	app.rotulosEnBorradores = new Map();
	for (const f of filas) anotarRotulo(f);
	const estacion = app.sesion?.estacion ?? null;
	return filas
		.filter((f) => TIPOS_DE_BORRADOR.has(f.tipo) && (estacion === null || estacionDeBorrador(f.tipo) === estacion))
		.sort((a, b) => a.actualizado_en - b.actualizado_en);
}

/** Descarta ESTE borrador y ninguno más: jamás los de otra pestaña (A5). */
export async function descartarBorrador(borrador: BorradorCualquiera): Promise<void> {
	// La foto del precinto de un borrador de apertura se va con él: un blob sin borrador
	// que lo reclame intentaría subir contra un silo que nunca existió (§5.3).
	const fotoId = borrador.tipo === 'abrir' ? (borrador.foto?.foto_id ?? null) : null;
	const tx = app.bd.transaction(['borradores', 'fotos'], 'readwrite');
	tx.objectStore('borradores').delete(borrador.capture_id);
	if (fotoId !== null) {
		// Solo si sigue sin confirmar: una copia vieja del borrador en otra pestaña no puede
		// borrar la evidencia de una apertura ya encolada (Codex, ronda del diff, 14/9).
		const fotos = tx.objectStore('fotos');
		const foto = (await promesa(fotos.get(fotoId))) as { confirmada?: boolean } | undefined;
		if (foto && !foto.confirmada) fotos.delete(fotoId);
	}
	await alCompletar(tx);
	app.rotulosEnBorradores.delete(borrador.capture_id);
}

export async function leerBorrador(captureId: string): Promise<Borrador | undefined> {
	const fila = (await promesa(app.bd.transaction('borradores').objectStore('borradores').get(captureId))) as BorradorCualquiera | undefined;
	return fila?.tipo === 'viaje' ? fila : undefined;
}
